import { Injectable, NgZone, Optional } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type AlertToastType = 'success' | 'error' | 'warning';

export interface AlertToast {
  id: number;
  message: string;
  type: AlertToastType;
}

const MAX_VISIBLE_TOASTS = 5;

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private nextId = 1;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  private readonly toastsSubject = new BehaviorSubject<AlertToast[]>([]);

  readonly toasts$ = this.toastsSubject.asObservable();

  constructor(@Optional() private readonly ngZone?: NgZone) {}

  success(message: string, duration: number = 3000): void {
    this.enqueue(message, 'success', duration);
  }

  error(message: string, duration: number = 5000): void {
    this.enqueue(message ? message : 'An error occurred', 'error', duration);
  }

  warning(message: string, duration: number = 5000): void {
    this.enqueue(message, 'warning', duration);
  }

  dismiss(id: number): void {
    this.runInZone(() => {
      this.clearTimer(id);
      this.toastsSubject.next(this.toastsSubject.value.filter((toast) => toast.id !== id));
    });
  }

  dismissAll(): void {
    this.runInZone(() => {
      for (const id of [...this.timers.keys()]) {
        this.clearTimer(id);
      }
      this.toastsSubject.next([]);
    });
  }

  private enqueue(message: string, type: AlertToastType, duration: number): void {
    this.runInZone(() => {
      const toast: AlertToast = { id: this.nextId++, message, type };
      const next = [...this.toastsSubject.value, toast];

      if (next.length > MAX_VISIBLE_TOASTS) {
        const removed = next.splice(0, next.length - MAX_VISIBLE_TOASTS);
        for (const dropped of removed) {
          this.clearTimer(dropped.id);
        }
      }

      this.toastsSubject.next(next);

      if (duration > 0) {
        const timer = setTimeout(() => this.dismiss(toast.id), duration);
        this.timers.set(toast.id, timer);
      }
    });
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }

  private runInZone(work: () => void): void {
    if (this.ngZone && !NgZone.isInAngularZone()) {
      this.ngZone.run(work);
      return;
    }
    work();
  }
}
